import { useMemo, useState } from 'react';
import { PivotTableChart, PlayArrow } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Paper, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
import { v4 as uuid } from 'uuid';
import { useAdminScenarioQuery, useCreateInvoicesFromJsonMutation } from '../../api/gql';
import { ConfirmationDialog } from '../../components/dialogs/ConfirmationDialog';
import { renderCellOrDefault } from '../../components/table/renderCellOrDefault';
import { Table } from '../../components/table/Table';
import { notify } from '../../utils/notification';
import { ModelForm } from '../forms/model/ModelForm';
import { UserForm } from '../forms/user/UserForm';
import { AdminScenarioInfoBox } from '../scenarios/ScenarioDetailOverview';
import { AdminScenariosAutocomplete } from '../scenarios/ScenariosSelectInput';

const TRANS_NS = 'universal-reminding';

type MappedDataType = { [key: string]: any }[];

export const DataMapperModule = () => {
  const { t } = useTranslation(TRANS_NS, { keyPrefix: 'data-mapper' });

  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [scenarioId, setScenarioId] = useState('');
  const [mappedData, setMappedData] = useState<MappedDataType>();
  const [mappedInvalidData, setMappedInvalidData] = useState<MappedDataType>();

  const translations = useLocalizedReactSpreadsheetImport();
  const theme = useThemedReactSpreadsheetImport();

  const { data } = useAdminScenarioQuery({ id: scenarioId }, { enabled: !!scenarioId });

  const reset = () => {
    setScenarioId('');
    setMappedData(undefined);
    setMappedInvalidData(undefined);
    setIsOpen(false);
  };

  const { mutate, isPending: isMutating } = useCreateInvoicesFromJsonMutation({
    onSuccess: () => {
      notify.positive({ message: t('mutation.success') });
      reset();
    },
    onError: () => {
      notify.negative({
        message: t('mutation.error'),
      });
    },
  });

  const fields = useMemo(() => {
    const modelFields = data?.adminScenario?.model?.fields;

    return (
      modelFields?.map((field) => ({
        label: field.name,
        key: field.name,
        fieldType: {
          type: 'input',
        },
        validations: [
          field.required
            ? {
                rule: 'required',
                errorMessage: `${field.name} ${t('isRequired')}`,
                level: 'error',
              }
            : {},
        ],
      })) ?? []
    );
  }, [data?.adminScenario?.model?.fields, t]);

  const onClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (data: MappedDataType) => {
    setMappedData(data);
    setIsOpen(false);
  };

  const startReminding = (confirmed: boolean) => {
    setIsConfirmationDialogOpen(false);

    if (!confirmed) {
      return;
    }

    if (!scenarioId || !mappedData) {
      throw new Error('Scenario ID and mapped data are required');
    }

    mutate({
      input: {
        scenarioId,
        invoices: mappedData,
      },
    });
  };

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Typography variant={'h2'}>{t('step.first')}</Typography>
        <AdminScenariosAutocomplete onScenarioSelected={setScenarioId} />
      </Stack>
      <Stack gap={2}>
        <Typography variant={'h2'}>{t('step.second')}</Typography>
        <Box>
          <Button
            disabled={!scenarioId}
            onClick={() => setIsOpen(true)}
            variant={mappedData ? 'outlined' : 'contained'}
            endIcon={<PivotTableChart />}
            size={'large'}
          >
            {t('mapAFile')}
          </Button>
        </Box>
        {/*  MODEL overview */}
        {data?.adminScenario.model ? (
          <Stack flex={1} gap={1}>
            <Typography fontWeight={'bold'}>{t('modelForMapping')}</Typography>
            <Paper sx={{ width: '100%', position: 'relative', p: 2 }}>
              <ModelForm key={data?.adminScenario.model.id} defaultValues={data?.adminScenario.model} readonly={true} />
            </Paper>
          </Stack>
        ) : null}

        {isOpen ? (
          <ReactSpreadsheetImport
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={(res) => {
              if (res.invalidData.length) {
                setMappedInvalidData(res.invalidData);
              }
              onSubmit(res.validData);
            }}
            fields={fields}
            translations={translations}
            customTheme={theme}
            rowHook={(data, addError) => {
              // Validation
              if (!data['EMAIL'] && !data['TELEFON']) {
                addError('EMAIL', {
                  message: t('validation.contact'),
                  level: 'error',
                });
                addError('TELEFON', {
                  message: t('validation.contact'),
                  level: 'error',
                });
              }
              // transformation
              if (data['KOLIK']) {
                data['KOLIK'] = sanitizeAmount(data['KOLIK'].toString());
              }
              return data;
            }}
          />
        ) : null}
      </Stack>

      <Stack gap={2}>
        <Typography variant={'h2'}>{t('step.third')}</Typography>
        <Box display={'flex'} justifyContent={'flex-start'} width={'100%'}>
          <LoadingButton
            loading={isMutating}
            disabled={!mappedData?.length}
            variant={'contained'}
            size={'large'}
            endIcon={<PlayArrow />}
            onClick={() => setIsConfirmationDialogOpen(true)}
          >
            {t('startReminding')}
          </LoadingButton>
        </Box>
        {/*Mapped data*/}
        {mappedData?.length ? (
          <Stack gap={1} width={'100%'}>
            <Typography fontWeight={'bold'}>{t('mappedData')}</Typography>
            <MappedDataTable mappedData={mappedData} />
          </Stack>
        ) : null}
        {mappedInvalidData?.length ? (
          <Stack gap={1} width={'100%'}>
            <Typography fontWeight={'bold'} color={'error'}>
              {t('mappedInvalidData')}
            </Typography>
            <MappedDataTable mappedData={mappedInvalidData} error={true} />
          </Stack>
        ) : null}
        <Stack direction={'row'} gap={2} flexWrap={'wrap'}>
          {/* Scenario overview */}
          {data?.adminScenario.user ? (
            <Stack flex={1} gap={1}>
              <Typography fontWeight={'bold'}>{t('user')}</Typography>
              <Paper sx={{ width: '100%', position: 'relative', p: 2 }}>
                <UserForm onSubmit={() => null} defaultValues={data?.adminScenario.user} readonly={true} />
              </Paper>
            </Stack>
          ) : null}
          {/*  Scenario overview */}
          {data?.adminScenario.id ? (
            <Stack flex={1} gap={1}>
              <Typography fontWeight={'bold'}>{t('scenario')}</Typography>
              <AdminScenarioInfoBox scenarioId={data.adminScenario.id} />
            </Stack>
          ) : null}
        </Stack>
      </Stack>
      <ConfirmationDialog
        title={t('confirmationDialog.title')}
        description={t('confirmationDialog.description')}
        onClose={startReminding}
        open={isConfirmationDialogOpen}
      />
    </Stack>
  );
};

const MappedDataTable = ({ mappedData, error }: { mappedData: MappedDataType; error?: boolean }) => {
  const columns: GridColDef[] = useMemo(() => {
    if (!mappedData.length) {
      return [];
    }
    const template = mappedData[0];
    return Object.keys(template).map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      renderCell: renderCellOrDefault,
    }));
  }, [mappedData]);

  return (
    <Table
      columns={columns}
      rows={mappedData}
      getRowId={() => uuid()}
      id={error ? 'mapped-data-table-error' : 'mapped-data-table'}
      sx={{ borderColor: error ? 'error.main' : undefined }}
      initialState={{
        pagination: { paginationModel: { pageSize: 15, page: 0 } },
      }}
    />
  );
};

const useThemedReactSpreadsheetImport = () => {
  const { palette, typography } = useTheme();

  const customTheme = {
    colors: {
      textColor: palette.text.primary,
      subtitleColor: palette.text.secondary,
      inactiveColor: palette.text.disabled,
      border: palette.grey.A200,
      background: palette.background.paper,
      rsi: {
        50: palette.primary.light,
        500: palette.primary.main,
        900: palette.primary.dark,
      },
    },
    fonts: {
      body: typography.fontFamily,
      heading: typography.fontFamily,
      mono: typography.fontFamily,
    },
  };

  return customTheme;
};

const useLocalizedReactSpreadsheetImport = () => {
  const { t } = useTranslation('react-spreadsheet-import');

  const translations = {
    uploadStep: {
      title: t('uploadStep.title'),
      manifestTitle: t('uploadStep.manifestTitle'),
      manifestDescription: t('uploadStep.manifestDescription'),
      maxRecordsExceeded: (maxRecords: number) => t('uploadStep.maxRecordsExceeded', { count: maxRecords }),
      dropzone: {
        title: t('uploadStep.dropzone.title'),
        errorToastDescription: t('uploadStep.dropzone.errorToastDescription'),
        activeDropzoneTitle: t('uploadStep.dropzone.activeDropzoneTitle'),
        buttonTitle: t('uploadStep.dropzone.buttonTitle'),
        loadingTitle: t('uploadStep.dropzone.loadingTitle'),
      },
      selectSheet: {
        title: t('uploadStep.selectSheet.title'),
        nextButtonTitle: t('uploadStep.selectSheet.nextButtonTitle'),
      },
    },
    selectHeaderStep: {
      title: t('selectHeaderStep.title'),
      nextButtonTitle: t('selectHeaderStep.nextButtonTitle'),
    },
    matchColumnsStep: {
      title: t('matchColumnsStep.title'),
      nextButtonTitle: t('matchColumnsStep.nextButtonTitle'),
      userTableTitle: t('matchColumnsStep.userTableTitle'),
      templateTitle: t('matchColumnsStep.templateTitle'),
      selectPlaceholder: t('matchColumnsStep.selectPlaceholder'),
      ignoredColumnText: t('matchColumnsStep.ignoredColumnText'),
      subSelectPlaceholder: t('matchColumnsStep.subSelectPlaceholder'),
      matchDropdownTitle: t('matchColumnsStep.matchDropdownTitle'),
      unmatched: t('matchColumnsStep.unmatched'),
      duplicateColumnWarningTitle: t('matchColumnsStep.duplicateColumnWarningTitle'),
      duplicateColumnWarningDescription: t('matchColumnsStep.duplicateColumnWarningDescription'),
    },
    validationStep: {
      title: t('validationStep.title'),
      nextButtonTitle: t('validationStep.nextButtonTitle'),
      noRowsMessage: t('validationStep.noRowsMessage'),
      noRowsMessageWhenFiltered: t('validationStep.noRowsMessageWhenFiltered'),
      discardButtonTitle: t('validationStep.discardButtonTitle'),
      filterSwitchTitle: t('validationStep.filterSwitchTitle'),
    },
    alerts: {
      confirmClose: {
        headerTitle: t('alerts.confirmClose.headerTitle'),
        bodyText: t('alerts.confirmClose.bodyText'),
        cancelButtonTitle: t('alerts.confirmClose.cancelButtonTitle'),
        exitButtonTitle: t('alerts.confirmClose.exitButtonTitle'),
      },
      submitIncomplete: {
        headerTitle: t('alerts.submitIncomplete.headerTitle'),
        bodyText: t('alerts.submitIncomplete.bodyText'),
        bodyTextSubmitForbidden: t('alerts.submitIncomplete.bodyTextSubmitForbidden'),
        cancelButtonTitle: t('alerts.submitIncomplete.cancelButtonTitle'),
        finishButtonTitle: t('alerts.submitIncomplete.finishButtonTitle'),
      },
      unmatchedRequiredFields: {
        headerTitle: t('alerts.unmatchedRequiredFields.headerTitle'),
        bodyText: t('alerts.unmatchedRequiredFields.bodyText'),
        listTitle: t('alerts.unmatchedRequiredFields.listTitle'),
        cancelButtonTitle: t('alerts.unmatchedRequiredFields.cancelButtonTitle'),
        continueButtonTitle: t('alerts.unmatchedRequiredFields.continueButtonTitle'),
      },
      toast: {
        error: t('alerts.toast.error'),
      },
    },
  };

  return translations;
};

const sanitizeAmount = (amount: string) => {
  // Remove all whitespace
  const withoutSpaces = amount.trim().replace(/\s/g, '');

  // Replace all commas with dots
  const withDots = withoutSpaces.replace(/,/g, '.');

  // If there are multiple dots, keep only the last one
  const lastDotIndex = withDots.lastIndexOf('.');
  if (lastDotIndex === -1) return withDots;

  const beforeLastDot = withDots.slice(0, lastDotIndex).replace(/\./g, '');
  const afterLastDot = withDots.slice(lastDotIndex);

  return beforeLastDot + afterLastDot;
};
